import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createWorker } from 'tesseract.js';
import { stockService } from '../services/api';
import './DeliveryNote.css';

const normalize = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const ignoredNameWords = new Set([
    'AVEC', 'DANS', 'DES', 'POUR', 'SANS', 'THE', 'WITH', 'WITHOUT',
    'PIECE', 'PIECES', 'SERVICE', 'MOIS', 'MONATE', 'MOBILE', 'ROUTEUR',
    'SPECIAL', 'EDITION', 'SEC', 'GATEWAY', 'NETWORK', 'APPLIANCE'
]);

const nameTokens = (value) => [...new Set(String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\+/g, ' PLUS ')
    .replace(/\bDOCKING\s*STATION\b/g, ' DOCK ')
    .replace(/\bDOCKINGSTATION\b/g, ' DOCK ')
    .replace(/\bSTATION\s+D\s+ACCUEIL\b/g, ' DOCK ')
    .replace(/\bUDM\s*SE\b/g, ' DREAM MACHINE ')
    .replace(/\bUNIFI\s+DREAM\s+MACHINE(?:\s+SPECIAL\s+EDITION)?\b/g, ' UBIQUITI DREAM MACHINE ')
    .replace(/\b(\d+)\s*(?:PT|PORT)\b/g, ' $1 PORTS ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(word => word.length >= 2 && !ignoredNameWords.has(word)))];

const designationSimilarity = (line, designation) => {
    const productWords = nameTokens(designation);
    if (productWords.length < 2) return 0;
    const lineWords = new Set(nameTokens(line));
    const matchingWords = productWords.filter(word => lineWords.has(word)).length;
    if (matchingWords < 2) return 0;
    const productCoverage = matchingWords / productWords.length;
    const lineCoverage = matchingWords / Math.max(1, Math.min(lineWords.size, productWords.length + 2));
    return Math.max(productCoverage, lineCoverage);
};

const findKnownFamilyProduct = (line, products) => {
    const value = normalize(line);
    const isDreamMachine = value.includes('UDMSE') ||
        value.includes('UNIFIDREAMMACHINE') ||
        value.includes('UBIQUITIDREAMMACHINE');
    if (!isDreamMachine) return null;

    return products.find(product => normalize(product.designation) === 'ROUTEURUBIQUITIDREAMMACHINE') ||
        products.find(product => {
            const designation = normalize(product.designation);
            return designation.includes('UBIQUITI') && designation.includes('DREAMMACHINE');
        }) || null;
};

let ocrWorkerPromise = null;
let ocrProgressListener = null;

const getOcrWorker = () => {
    if (!ocrWorkerPromise) {
        ocrWorkerPromise = createWorker('fra+eng', 1, {
            logger: message => {
                if (message.status === 'recognizing text' && ocrProgressListener) {
                    ocrProgressListener(message.progress || 0);
                }
            },
        }).catch(error => {
            ocrWorkerPromise = null;
            throw error;
        });
    }
    return ocrWorkerPromise;
};

const rotateImageForOcr = async (file, degrees) => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const source = await new Promise((resolve, reject) => {
            const element = new Image();
            element.onload = () => resolve(element);
            element.onerror = () => reject(new Error("Impossible de lire l’orientation du document."));
            element.src = objectUrl;
        });
        const normalizedDegrees = ((degrees % 360) + 360) % 360;
        const swapSides = normalizedDegrees === 90 || normalizedDegrees === 270;
        const canvas = document.createElement('canvas');
        canvas.width = swapSides ? source.naturalHeight : source.naturalWidth;
        canvas.height = swapSides ? source.naturalWidth : source.naturalHeight;
        const context = canvas.getContext('2d');
        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate(normalizedDegrees * Math.PI / 180);
        context.drawImage(source, -source.naturalWidth / 2, -source.naturalHeight / 2);
        return canvas;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

const ocrQualityScore = text => {
    const normalizedText = String(text || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const words = normalizedText.match(/[a-z]{3,}/g) || [];
    const usefulWords = new Set([
        'livraison', 'commande', 'expedition', 'reference', 'designation',
        'quantite', 'piece', 'produit', 'article', 'client', 'adresse',
        'document', 'numero', 'date', 'total', 'ean', 'serie', 'fournisseur'
    ]);
    const usefulMatches = words.filter(word => usefulWords.has(word)).length;
    const itemLines = (normalizedText.match(/^\s*\d+\s*(?:x|piece|pieces|pcs|unite|unites)\b/gm) || []).length;
    return words.length + usefulMatches * 35 + itemLines * 25;
};

const extractTextWithOcr = async (file, onProgress) => {
    ocrProgressListener = onProgress;
    try {
        const worker = await getOcrWorker();
        ocrProgressListener = value => onProgress(value * 0.5);
        const originalResult = await worker.recognize(file);
        const originalText = String(originalResult?.data?.text || '').trim();

        const rotatedImage = await rotateImageForOcr(file, 180);
        ocrProgressListener = value => onProgress(0.5 + value * 0.5);
        const rotatedResult = await worker.recognize(rotatedImage);
        const rotatedText = String(rotatedResult?.data?.text || '').trim();

        const useRotated = ocrQualityScore(rotatedText) > ocrQualityScore(originalText);
        return {
            text: useRotated ? rotatedText : originalText,
            rotation: useRotated ? 180 : 0,
        };
    } finally {
        ocrProgressListener = null;
    }
};

const addOcrStockMatches = (found, ocrText, products) => {
    const lines = String(ocrText || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    lines.forEach((line, index) => {
        const normalizedLine = line
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/^[•·]\s*/, '');
        const quantityMatch = normalizedLine.match(/^(\d+)\s*(?:x\s*)?(?:piece(?:s)?|pcs?|unite(?:s)?)\b/i);
        if (!quantityMatch) return;

        const quantity = Number.parseInt(quantityMatch[1], 10);
        if (!Number.isInteger(quantity) || quantity < 1) return;
        const sourceLine = lines.slice(index, index + 3).join(' ');
        if (/\b(eco[- ]?participation|contribution|bonus|promotion|promo|service|commission|remise|ausgleichsartikel)\b/i.test(sourceLine)) return;

        const knownFamilyProduct = findKnownFamilyProduct(sourceLine, products);
        const candidates = knownFamilyProduct ? [] : products
            .map(candidate => ({ candidate, score: designationSimilarity(sourceLine, candidate.designation) }))
            .filter(candidate => candidate.score >= 0.62)
            .sort((a, b) => b.score - a.score);
        if (!knownFamilyProduct && (!candidates[0] || (candidates[1] && candidates[0].score - candidates[1].score < 0.1))) return;

        const product = knownFamilyProduct || candidates[0].candidate;
        const score = knownFamilyProduct ? 0.95 : candidates[0].score;
        const id = String(product.id);
        if (found.has(id)) return;
        found.set(id, {
            id,
            quantity,
            sourceLine,
            matchMethod: knownFamilyProduct ? 'famille Dream Machine' : 'OCR + stock',
            confidence: Math.min(0.95, score),
        });
    });
};

const matchAiItems = (items, products, ocrText = '') => {
    const found = new Map();
    const unmatchedReferences = [];
    const unmatchedItems = new Map();
    items.forEach(item => {
        const reference = normalize(item.reference);
        const barcode = normalize(item.barcode);
        let product = products.find(candidate =>
            (reference.length >= 3 && normalize(candidate.ref_produit) === reference) ||
            (barcode.length >= 8 && normalize(candidate.code_barre) === barcode)
        );
        let matchMethod = 'reference IA';
        if (!product) {
            product = findKnownFamilyProduct([item.designation, item.source_text].filter(Boolean).join(' '), products);
            if (product) matchMethod = 'famille Dream Machine';
        }
        if (!product && item.designation) {
            const candidates = products
                .map(candidate => ({ candidate, score: designationSimilarity(item.designation, candidate.designation) }))
                .filter(candidate => candidate.score >= 0.55)
                .sort((a, b) => b.score - a.score);
            if (candidates[0] && (!candidates[1] || candidates[0].score - candidates[1].score >= 0.1)) {
                product = candidates[0].candidate;
                matchMethod = 'rapprochement IA';
            }
        }
        if (!product) {
            if (item.reference || item.barcode) unmatchedReferences.push(item.reference || item.barcode);
            const missingKey = normalize(item.reference || item.barcode || item.designation || item.source_text);
            if (missingKey && !unmatchedItems.has(missingKey)) {
                unmatchedItems.set(missingKey, {
                    key: missingKey,
                    reference: String(item.reference || item.barcode || '').trim(),
                    barcode: String(item.barcode || '').trim(),
                    designation: String(item.designation || '').trim(),
                    quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
                    confidence: Number(item.confidence) || 0,
                    sourceLine: String(item.source_text || '').trim(),
                });
            }
            return;
        }
        const id = String(product.id);
        const quantity = Math.max(1, Number.parseInt(item.quantity, 10) || 1);
        const previous = found.get(id);
        found.set(id, {
            id,
            quantity: (previous?.quantity || 0) + quantity,
            sourceLine: item.source_text || [item.reference, item.designation].filter(Boolean).join(' — '),
            matchMethod,
            confidence: Number(item.confidence) || 0,
        });
    });

    // Filet de securite : une designation coupee sur plusieurs lignes peut etre
    // visible dans l'OCR mais omise du JSON par le modele Vision.
    addOcrStockMatches(found, ocrText, products);

    return {
        rows: [...found.values()],
        unmatchedReferences: [...new Set(unmatchedReferences)],
        unmatchedItems: [...unmatchedItems.values()],
    };
};

const DeliveryNote = () => {
    const [products, setProducts] = useState([]);
    const [image, setImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [rawText, setRawText] = useState('');
    const [rows, setRows] = useState([]);
    const [unmatchedReferences, setUnmatchedReferences] = useState([]);
    const [unmatchedItems, setUnmatchedItems] = useState([]);
    const [creatingProductKey, setCreatingProductKey] = useState('');
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [isReading, setIsReading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [scannerStats, setScannerStats] = useState(null);
    const scannerBusy = useRef(false);

    useEffect(() => {
        stockService.getAllProducts()
            .then(data => setProducts(Array.isArray(data) ? data : []))
            .catch(() => setStatus("Impossible de charger l'inventaire."));
    }, []);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    useEffect(() => {
        const refreshScannerStatus = () => stockService.getScannerStatus()
            .then(setScannerStats)
            .catch(() => setScannerStats(null));
        refreshScannerStatus();
        const timer = window.setInterval(refreshScannerStatus, 5000);
        return () => window.clearInterval(timer);
    }, []);

    const productById = useMemo(
        () => new Map(products.map(product => [String(product.id), product])),
        [products]
    );

    const chooseImage = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setStatus('Choisis une photo au format image.');
            return;
        }
        if (file.size > 12 * 1024 * 1024) {
            setStatus('La photo ne doit pas depasser 12 Mo.');
            return;
        }
        setImage(file);
        setPreviewUrl(URL.createObjectURL(file));
        setRows([]);
        setUnmatchedReferences([]);
        setUnmatchedItems([]);
        setRawText('');
        setProgress(0);
        setStatus('Photo prete a etre analysee.');
    };

    const readImage = async (fileToRead = image) => {
        if (!fileToRead || isReading) return;
        setIsReading(true);
        setProgress(5);
        setStatus('OCR en cours : lecture complète du document...');
        let progressTimer;

        try {
            const ocrResult = await extractTextWithOcr(fileToRead, value => {
                setProgress(Math.max(5, Math.round(5 + value * 35)));
            });
            const ocrText = ocrResult.text;
            if (!ocrText) throw new Error("L’OCR n’a trouvé aucun texte lisible sur le document.");
            setProgress(42);
            setStatus('OCR terminé. L’IA structure et rapproche toutes les lignes...');
            setProgress(48);
            progressTimer = window.setInterval(() => {
                setProgress(current => Math.min(90, current + 3));
            }, 900);
            const analysis = await stockService.analyzeDeliveryWithAi(fileToRead, ocrText);
            setProgress(85);
            const extraction = matchAiItems(Array.isArray(analysis.items) ? analysis.items : [], products, ocrText);
            const detectedRows = extraction.rows;
            setRawText(JSON.stringify({ ...analysis, ocr_rotation: ocrResult.rotation, ocr_text: ocrText }, null, 2));
            setRows(detectedRows);
            setUnmatchedReferences(extraction.unmatchedReferences);
            setUnmatchedItems(extraction.unmatchedItems.map(item => ({
                ...item,
                supplier: String(analysis.supplier || '').trim(),
            })));
            setProgress(100);
            const document = [analysis.supplier, analysis.document_number].filter(Boolean).join(' — ');
            setStatus(detectedRows.length
                ? `${detectedRows.length} produit(s) rapproché(s) par l’IA${document ? ` pour ${document}` : ''}. Vérifie les quantités avant validation.`
                : "L’IA a lu le document, mais aucune ligne ne correspond avec certitude à l’inventaire. Ajoute ou corrige les lignes manuellement.");
            return true;
        } catch (error) {
            console.error(error);
            setStatus(error.message || "L’analyse IA a échoué. Reprends une photo nette et bien éclairée.");
            return false;
        } finally {
            window.clearInterval(progressTimer);
            setIsReading(false);
        }
    };

    useEffect(() => {
        if (!products.length || image || isReading) return undefined;

        const checkFtpQueue = async () => {
            if (scannerBusy.current) return;
            scannerBusy.current = true;
            try {
                const scan = await stockService.getNextScannedDelivery();
                if (!scan?.file) return;
                const response = await fetch(scan.file.data_url);
                const blob = await response.blob();
                const file = new File([blob], scan.file.name, { type: blob.type, lastModified: Date.now() });
                chooseImage(file);
                setStatus(`Nouveau BL reçu par FTP : ${file.name}. Analyse automatique en cours...`);
                const success = await readImage(file);
                await stockService.finishScannedDelivery(scan.file.token, success);
            } catch (error) {
                console.error(error);
                setStatus(error.message || "Impossible de récupérer le BL envoyé par le scanner.");
            } finally {
                scannerBusy.current = false;
            }
        };

        checkFtpQueue();
        const timer = window.setInterval(checkFtpQueue, 5000);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, image, isReading]);

    const addRow = () => {
        const firstUnused = products.find(product => !rows.some(row => row.id === String(product.id)));
        if (firstUnused) setRows(current => [...current, { id: String(firstUnused.id), quantity: 1, sourceLine: '', matchMethod: 'manual' }]);
    };

    const retryLatestScan = async () => {
        try {
            const hasQueuedScan = Number(scannerStats?.queued || 0) > 0;
            if (!hasQueuedScan) await stockService.retryLatestScannedDelivery();
            setImage(null);
            setPreviewUrl('');
            setRows([]);
            setUnmatchedReferences([]);
            setUnmatchedItems([]);
            setRawText('');
            setProgress(0);
            setStatus(hasQueuedScan
                ? 'Reprise du BL en attente. Analyse automatique en cours...'
                : 'Le dernier BL a été remis dans la file. Analyse automatique en cours...');
            setScannerStats(current => current ? { ...current, queued: 1 } : current);
        } catch (error) {
            setStatus(error.message);
        }
    };

    const updateRow = (index, field, value) => {
        setRows(current => current.map((row, rowIndex) => rowIndex === index
            ? { ...row, [field]: value }
            : row));
    };

    const removeRow = (index) => setRows(current => current.filter((_, rowIndex) => rowIndex !== index));

    const updateUnmatchedItem = (index, field, value) => {
        setUnmatchedItems(current => current.map((item, itemIndex) => itemIndex === index
            ? { ...item, [field]: value }
            : item));
    };

    const createUnmatchedProduct = async (item, index) => {
        const reference = String(item.reference || item.barcode || '').trim();
        const designation = String(item.designation || '').trim();
        if (!reference || !designation) {
            setStatus('Vérifie la désignation et la référence avant de créer ce produit.');
            return;
        }

        setCreatingProductKey(item.key);
        try {
            const result = await stockService.addProduct({
                ref_produit: reference,
                code_barre: String(item.barcode || '').trim(),
                designation,
                categorie: '',
                fournisseur: String(item.supplier || '').trim(),
                prix_achat_ht: 0,
                prix_vente_ht: 0,
                stock_actuel: 0,
                stock_min: 2,
            });
            if (!result?.success || !result.id) throw new Error(result?.message || 'Impossible de créer le produit.');

            const productId = String(result.id);
            const refreshedProducts = await stockService.getAllProducts();
            if (Array.isArray(refreshedProducts)) setProducts(refreshedProducts);
            setRows(current => [...current, {
                id: productId,
                quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
                sourceLine: item.sourceLine,
                matchMethod: 'produit créé depuis le BL',
                confidence: item.confidence,
            }]);
            setUnmatchedItems(current => current.filter((_, itemIndex) => itemIndex !== index));
            setUnmatchedReferences(current => current.filter(value => normalize(value) !== normalize(item.reference || item.barcode)));
            setStatus(`Le produit « ${designation} » a été créé. Il est prêt à être réceptionné avec la quantité ${item.quantity}.`);
        } catch (error) {
            setStatus(error.message || 'Impossible de créer le produit.');
        } finally {
            setCreatingProductKey('');
        }
    };

    const saveDelivery = async () => {
        const items = rows.map(row => ({ id: Number(row.id), quantity: Number(row.quantity) }));
        const invalid = items.some(item => !Number.isInteger(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1);
        if (!items.length || invalid) {
            setStatus('Verifie les produits et saisis des quantites entieres superieures a zero.');
            return;
        }
        if (!window.confirm(`Ajouter ${items.reduce((sum, item) => sum + item.quantity, 0)} unite(s) au stock ?`)) return;

        setIsSaving(true);
        try {
            const result = await stockService.receiveDelivery(items);
            setStatus(`Livraison enregistree : ${result.updated.length} produit(s) mis a jour.`);
            setRows([]);
            setUnmatchedReferences([]);
            setUnmatchedItems([]);
            setImage(null);
            setPreviewUrl('');
            setRawText('');
        } catch (error) {
            setStatus(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <main className="delivery-page">
            <section className="delivery-card">
                <div className="delivery-heading">
                    <div>
                        <p className="delivery-kicker">Entree de marchandises</p>
                        <h1>Analyser un bon de livraison</h1>
                        <p>Prends une photo : l’IA comprend les références et quantités, puis tu contrôles chaque ligne avant de modifier le stock.</p>
                    </div>
                    <label className="camera-button">
                        Prendre une photo
                        <input type="file" accept="image/*" capture="environment" onChange={event => chooseImage(event.target.files[0])} />
                    </label>
                </div>

                <div className="scanner-folder-card">
                    <div>
                        <strong>Scanner Brother connecté par FTP</strong>
                        <span>{scannerStats
                            ? `${scannerStats.queued} en attente · ${scannerStats.processing} en cours · ${scannerStats.archived} archivé(s) · ${scannerStats.rejected} rejeté(s)`
                            : 'Connexion à la file du scanner...'}</span>
                    </div>
                    {!isReading && (scannerStats?.queued > 0 || scannerStats?.processing > 0 || scannerStats?.archived > 0 || scannerStats?.rejected > 0) && (
                        <button type="button" className="secondary-button" onClick={retryLatestScan}>
                            {scannerStats?.queued > 0 ? 'Traiter le BL en attente' : 'Relancer le dernier BL'}
                        </button>
                    )}
                    <i className={scannerStats ? 'scanner-online' : ''} aria-hidden="true" />
                </div>

                {previewUrl && (
                    <div className="scan-grid">
                        <img className="delivery-preview" src={previewUrl} alt="Bon de livraison a analyser" />
                        <div className="scan-actions">
                            <button type="button" className="primary-button" onClick={() => readImage()} disabled={isReading || !products.length}>
                                {isReading ? `OCR + analyse IA... ${progress}%` : 'Analyser avec OCR + IA'}
                            </button>
                            {isReading && <progress value={progress} max="100">{progress}%</progress>}
                            <p>L’OCR lit d’abord tout le document, puis Groq recoupe le texte avec la photo. Aucune quantité n’est modifiée avant ta validation.</p>
                        </div>
                    </div>
                )}

                {status && <div className="delivery-status" role="status">{status}</div>}
                {unmatchedReferences.length > 0 && (
                    <div className="delivery-warning" role="alert">
                        L’IA a reconnu des références absentes du stock : {unmatchedReferences.join(', ')}.
                        Vérifie les informations ci-dessous, puis crée le produit en un clic.
                    </div>
                )}

                {unmatchedItems.length > 0 && (
                    <section className="missing-products" aria-labelledby="missing-products-title">
                        <div className="missing-products-heading">
                            <div>
                                <h2 id="missing-products-title">Produits à créer</h2>
                                <p>Ces articles sont présents sur le BL mais pas dans l’inventaire.</p>
                            </div>
                        </div>
                        {unmatchedItems.map((item, index) => (
                            <article className="missing-product-card" key={item.key}>
                                <div className="missing-product-fields">
                                    <label>
                                        Désignation
                                        <input value={item.designation} onChange={event => updateUnmatchedItem(index, 'designation', event.target.value)} />
                                    </label>
                                    <label>
                                        Référence
                                        <input value={item.reference} onChange={event => updateUnmatchedItem(index, 'reference', event.target.value)} />
                                    </label>
                                    <label>
                                        Code-barres
                                        <input value={item.barcode} onChange={event => updateUnmatchedItem(index, 'barcode', event.target.value)} />
                                    </label>
                                    <label>
                                        Quantité livrée
                                        <input type="number" min="1" step="1" value={item.quantity} onChange={event => updateUnmatchedItem(index, 'quantity', event.target.value)} />
                                    </label>
                                </div>
                                {item.sourceLine && <small title={item.sourceLine}>Lu par l’IA : {item.sourceLine}</small>}
                                <button
                                    type="button"
                                    className="create-product-button"
                                    disabled={creatingProductKey === item.key}
                                    onClick={() => createUnmatchedProduct(item, index)}
                                >
                                    {creatingProductKey === item.key ? 'Création...' : 'Créer le produit et préparer sa réception'}
                                </button>
                            </article>
                        ))}
                    </section>
                )}

                {(rows.length > 0 || rawText) && (
                    <section className="review-section">
                        <div className="review-title">
                            <h2>Lignes a receptionner</h2>
                            <button type="button" className="secondary-button" onClick={addRow}>+ Ajouter une ligne</button>
                        </div>

                        <div className="delivery-table-wrap">
                            <table className="delivery-table">
                                <thead><tr><th>Produit</th><th>Stock actuel</th><th>Quantite livree</th><th></th></tr></thead>
                                <tbody>
                                    {rows.map((row, index) => {
                                        const product = productById.get(row.id);
                                        return (
                                            <tr key={`${row.id}-${index}`}>
                                                <td>
                                                    <select value={row.id} onChange={event => updateRow(index, 'id', event.target.value)}>
                                                        {products.map(item => <option key={item.id} value={item.id}>{item.ref_produit} - {item.designation}</option>)}
                                                    </select>
                                                    {row.sourceLine && (
                                                        <small title={row.sourceLine}>
                                                            IA ({row.matchMethod}{row.confidence ? `, confiance ${Math.round(row.confidence * 100)} %` : ''}) : {row.sourceLine}
                                                        </small>
                                                    )}
                                                </td>
                                                <td>{product?.stock_actuel ?? '-'}</td>
                                                <td><input type="number" min="1" step="1" value={row.quantity} onChange={event => updateRow(index, 'quantity', event.target.value)} /></td>
                                                <td><button type="button" className="remove-button" onClick={() => removeRow(index)} aria-label="Supprimer la ligne">×</button></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <button type="button" className="save-delivery-button" onClick={saveDelivery} disabled={isSaving || !rows.length}>
                            {isSaving ? 'Enregistrement...' : 'Valider et ajouter au stock'}
                        </button>

                        <details className="raw-text"><summary>Voir l’analyse structurée de l’IA</summary><pre>{rawText}</pre></details>
                    </section>
                )}
            </section>
        </main>
    );
};

export default DeliveryNote;
